// ============================================================
// Handy-Erfassung: Foto/Dokument/Beleg aufnehmen, clientseitig
// verkleinern, per Claude Vision (teil-)automatisch klassifizieren
// und NACH FREIGABE durch den Nutzer in die Firmen-Akte legen.
//
// DSGVO (Klasse Grün): Nur administrative Dokumente (Belege,
// Rechnungen, Lieferscheine, Visitenkarten, Begehungsfotos) an die
// KI senden — niemals Befunde oder Patientendaten. Der Nutzer
// bestätigt das vor jedem KI-Aufruf; ohne KI ist manuelles Ablegen
// jederzeit möglich.
// ============================================================

import { db, type Mandant } from '../db/schema';
import { getAnthropicKey } from './documentImportService';
import { loadMandanten } from './mandantenService';
import { logAktion } from './auditLogService';
import type { MandantID } from '../types/branded';

const MAX_DIM = 1600;          // längste Bildkante nach Verkleinerung
const JPEG_QUALITY = 0.8;
const MAX_PDF_BYTES = 4 * 1024 * 1024;

export interface CaptureAnhang {
  b64: string;
  mime: string;
}

export interface KlassifizierungsVorschlag {
  typ: 'BELEG' | 'DOKUMENT';
  mandant_id: MandantID | null;
  titel: string;
  datum: string | null;         // ISO-Datum falls am Dokument erkennbar
  zusammenfassung: string;
  naechster_schritt: string;    // KI-Vorschlag für die Folge-Aktion
  betrag_cents: number | null;  // bei BELEG: erkannter Bruttobetrag
}

// ── Bild verkleinern (Kamera-Fotos sind 3–10 MB — zu groß für Sync) ───────

export async function prepareAnhang(file: File): Promise<CaptureAnhang> {
  const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

  if (isPdf) {
    if (file.size > MAX_PDF_BYTES) {
      throw new Error('PDF zu groß (max. 4 MB). Bitte als Foto aufnehmen oder verkleinern.');
    }
    return { b64: await fileToB64(file), mime: 'application/pdf' };
  }

  if (!file.type.startsWith('image/')) {
    throw new Error('Dateityp nicht unterstützt. Bitte Foto (JPG/PNG) oder PDF verwenden.');
  }

  const img = await loadImage(file);
  const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('Canvas nicht verfügbar.');
  ctx.drawImage(img, 0, 0, w, h);

  const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  const b64 = dataUrl.split(',')[1];
  if (b64 === undefined) throw new Error('Bild-Konvertierung fehlgeschlagen.');
  return { b64, mime: 'image/jpeg' };
}

function fileToB64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = (reader.result as string).split(',')[1];
      if (b64 === undefined) { reject(new Error('Base64-Konvertierung fehlgeschlagen')); return; }
      resolve(b64);
    };
    reader.onerror = () => { reject(new Error('Datei konnte nicht gelesen werden')); };
    reader.readAsDataURL(file);
  });
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Bild konnte nicht geladen werden')); };
    img.src = url;
  });
}

// ── KI-Klassifizierung (Freigabe-Vorschlag, kein Auto-Speichern) ──────────

export async function classifyCapture(anhang: CaptureAnhang): Promise<KlassifizierungsVorschlag> {
  const apiKey = getAnthropicKey();
  if (apiKey === null) {
    throw new Error('Kein Anthropic API Key konfiguriert (Einstellungen → Sicherheit).');
  }

  const mandanten = await loadMandanten();
  const firmenListe = mandanten
    .map(m => `- ${m.kurz_code}: ${m.name} (Aliase: ${m.title_aliases.join(', ') || '—'})`)
    .join('\n');

  const prompt = `Analysiere dieses Dokument/Foto aus einer österreichischen Arbeitsmedizin-Praxis.
Gib das Ergebnis AUSSCHLIESSLICH als JSON zurück, kein erklärender Text.

JSON-Schema:
{
  "typ": "BELEG" | "DOKUMENT",
  "mandant_kurz_code": string | null,
  "titel": string,
  "datum": string | null,
  "zusammenfassung": string,
  "naechster_schritt": string,
  "betrag_euro": number | null
}

Regeln:
- "BELEG" = Rechnung, Kassenbon, Lieferschein, Quittung, Tankbeleg
- "DOKUMENT" = alles andere (Brief, Vertrag, Foto einer Begehung, Visitenkarte, Notiz)
- mandant_kurz_code: Ordne einer dieser Firmen zu (exakt den Kurz-Code zurückgeben), oder null wenn keine passt:
${firmenListe}
- titel: kurzer sprechender Titel, z.B. "Rechnung Bürobedarf €142,50" oder "Begehungsfoto Halle 2"
- datum: Dokumentdatum als YYYY-MM-DD falls erkennbar, sonst null
- zusammenfassung: 1–3 Sätze Kerninhalt (Beträge, Absender, Zweck)
- betrag_euro: bei BELEG der Brutto-Gesamtbetrag als Zahl (z.B. 142.50), sonst null
- naechster_schritt: kurzer Vorschlag für die sinnvollste Folge-Aktion, passend zum Typ:
  * Beleg/Rechnung → "Zur Steuerberater-Ausgabe vormerken"
  * Visitenkarte/Firmenbriefkopf → "Als Mandant anlegen"
  * Dienstzettel/Personalbogen → "Als Mitarbeiter anlegen"
  * Brief mit Frist/Termin → "Aufgabe/Erinnerung anlegen: <kurz>"
  * sonst → "Nur in Akte ablegen"
- Handschrift sorgfältig entziffern

Gib NUR den JSON-Block zurück.`;

  type ContentBlock =
    | { type: 'image';    source: { type: 'base64'; media_type: string; data: string } }
    | { type: 'document'; source: { type: 'base64'; media_type: string; data: string } }
    | { type: 'text';     text: string };

  const docBlock: ContentBlock = anhang.mime === 'application/pdf'
    ? { type: 'document', source: { type: 'base64', media_type: anhang.mime, data: anhang.b64 } }
    : { type: 'image',    source: { type: 'base64', media_type: anhang.mime, data: anhang.b64 } };

  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'content-type': 'application/json',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: 'claude-haiku-4-5-20251001',
      max_tokens: 512,
      messages: [{ role: 'user', content: [docBlock, { type: 'text', text: prompt }] }],
    }),
  });

  if (!response.ok) {
    if (response.status === 401) throw new Error('Ungültiger Anthropic API Key.');
    if (response.status === 429) throw new Error('API-Limit erreicht — kurz warten.');
    throw new Error(`KI-Fehler ${response.status}`);
  }

  const result = await response.json() as { content: Array<{ type: string; text?: string }> };
  const raw = result.content.find(c => c.type === 'text')?.text ?? '';
  const clean = raw.replace(/^```(?:json)?\s*/m, '').replace(/\s*```\s*$/m, '').trim();

  let parsed: {
    typ?: string; mandant_kurz_code?: string | null;
    titel?: string; datum?: string | null; zusammenfassung?: string;
    naechster_schritt?: string; betrag_euro?: number | null;
  };
  try {
    parsed = JSON.parse(clean) as typeof parsed;
  } catch {
    throw new Error('KI-Antwort nicht lesbar — bitte manuell einordnen.');
  }

  const mandant = mandanten.find(
    m => m.kurz_code.toUpperCase() === (parsed.mandant_kurz_code ?? '').toUpperCase(),
  );

  const typ = parsed.typ === 'BELEG' ? 'BELEG' : 'DOKUMENT';
  const betragCents = typeof parsed.betrag_euro === 'number' && parsed.betrag_euro > 0
    ? Math.round(parsed.betrag_euro * 100)
    : null;

  void logAktion('SYSTEM', 'AKTE_KI_KLASSIFIZIERUNG', { typ });

  return {
    typ,
    mandant_id: mandant?.id ?? null,
    titel: parsed.titel?.trim() || 'Erfasstes Dokument',
    datum: parsed.datum ?? null,
    zusammenfassung: parsed.zusammenfassung?.trim() ?? '',
    naechster_schritt: parsed.naechster_schritt?.trim()
      || (typ === 'BELEG' ? 'Zur Steuerberater-Ausgabe vormerken' : 'Nur in Akte ablegen'),
    betrag_cents: betragCents,
  };
}

// ── Ablage nach Freigabe ───────────────────────────────────────────────────

export async function saveCapture(input: {
  mandant_id: MandantID;
  typ: 'BELEG' | 'DOKUMENT';
  titel: string;
  datum: string;                // ISO
  inhalt: string;
  anhang: CaptureAnhang;
  naechster_schritt?: string;
  betrag_cents?: number | null;
}): Promise<void> {
  await db.akte.add({
    id: crypto.randomUUID(),
    schema_version: 1,
    mandant_id: input.mandant_id,
    typ: input.typ,
    titel: input.titel,
    inhalt: input.inhalt,
    datum: input.datum,
    anhang_b64: input.anhang.b64,
    anhang_mime: input.anhang.mime,
    ...(input.naechster_schritt !== undefined && input.naechster_schritt !== ''
      ? { naechster_schritt: input.naechster_schritt } : {}),
    erledigt: false,
    ...(input.betrag_cents !== undefined && input.betrag_cents !== null
      ? { betrag_cents: input.betrag_cents } : {}),
    erstellt_am: new Date().toISOString(),
  });
  void logAktion('SYSTEM', 'AKTE_ERFASSUNG_ABGELEGT', { typ: input.typ });
}

export type { Mandant };
